import React, { useEffect, useState } from 'react'
import { Container, Textarea, Button, Popup } from 'components/UIElements'
import '../stylesheets/createStudy.scss'
import STRINGS from 'utils/strings'
import { MODAL_CLASSES_MAP } from 'utils/constants'
import EditStudyVersionPopupContainer from '../containers/EditStudyVersionPopupContainer'
import { generateNotyMessage } from 'utils/request'
import { displayStudyVersion, isVersionLessThanOrEqualTo } from '../utils/studyUtils'

const StudyVersionDetailsSection = ({
  study,
  openModal,
  closeModal,
  studyVersionList,
  fetchStudyVersionList,
  promoteStudyToUat,
  promoteStudyToLive,
  saveStudyVersionNotes,
  hasAccessToEditVersionNotes = false,
  studyVersionID,
  showError,
}) => {
  const [notes, setNotes] = useState('')
  const [error, setError] = useState('')
  const studyId = study?.id

  const getCurrentVersion = () => {
    return studyVersionList?.find(
      item =>
        String(item?.version_id) === String(studyVersionID) || String(item?.id) === String(studyVersionID),
    )
  }

  const currentVersion = getCurrentVersion()

  useEffect(() => {
    if (currentVersion?.notes) {
      setNotes(currentVersion?.notes)
    }
  }, [currentVersion?.notes])

  const isCurrentUAT = currentVersion?.version_status.toLowerCase() === 'uat'
  const isCurrentDEV = currentVersion?.version_status.toLowerCase() === 'dev'
  const isCurrentLive = currentVersion?.version_status.toLowerCase() === 'live'

  const handleCreateNewVersionClick = async () => {
    if (isCurrentUAT) {
      await promoteStudyToLive(studyId)
    }

    if (isCurrentDEV) {
      await promoteStudyToUat(studyId, notes)
    }
  }

  const handleSaveNotesClick = async () => {
    if (isCurrentDEV) {
      await saveStudyVersionNotes(studyId, notes)
      await fetchStudyVersionList(studyId)
    }
  }

  const hasLiveVersionConflict = () => {
    if (isCurrentUAT && currentVersion?.version_display_number) {
      return studyVersionList?.some((v) => {
        const status = v.version_status.toLowerCase()
        return (
          status === 'live' || status === 'dev'
        ) && isVersionLessThanOrEqualTo(currentVersion.version_display_number, v.version_display_number)
      })
    }
    return false
  }

  const studyVersion = currentVersion
    ? `${currentVersion?.version_status} ${displayStudyVersion(currentVersion?.version_display_number)}`
    : null

  const isEditable = !currentVersion || isCurrentDEV

  const versionNotesInputDisabled = !isEditable || !hasAccessToEditVersionNotes

  const notesAreSaved = notes === currentVersion?.notes

  const handleNotesChange = (value) => {
    if (!!error) {
      setError('')
    }
    setNotes(value)
  }

  return (
    <Container className='study-version-details-section'>
      <h3>{STRINGS.studyVersionDetails}</h3>
      <Textarea
        className={`study-version-change-notes ${versionNotesInputDisabled ? 'disabled' : ''}`}
        hasError={!!error}
        errorText={error}
        label={STRINGS.versionChange}
        disabled={versionNotesInputDisabled}
        value={notes}
        onChange={handleNotesChange}
        id='study-version-change-notes'
        placeholder={STRINGS.versionChangeNotes}
        noResize
      />
      <div className='buttons-box-wrapper save-notes-btn'>
        {isCurrentDEV && (
          <Button
            id='save-notes-study'
            className='save-notes-study-btn'
            disabled={!hasAccessToEditVersionNotes || !notes || notes === currentVersion?.notes}
            content={STRINGS.saveNotes}
            onClick={() => {
              if (!notes) {
                setError('Version change notes cannot be blank')

                showError({ text: generateNotyMessage('Please check errors before proceeding', false) })

                return
              }

              handleSaveNotesClick()
            }}
          />
        )}
      </div>
      <div className='buttons-box-wrapper'>
        <div className='buttons-box'>
          {!!studyVersion ? (
            <div className='edit-state-box'>
              <div className='edit-state-title'>{studyVersion}</div>
              {isEditable ? (
                <div className='edit-state-icon'>
                  <Popup
                    dark
                    noPointer={false}
                    position='right'
                    className='edit'
                    disabled={!hasAccessToEditVersionNotes}
                    trigger={<Button className='edit-state-button' link icon='fas fa-pencil-alt' />}>
                    <>
                      <Button
                        className='edit-state-button-content'
                        content={STRINGS.editStudyVersion}
                        onClick={() => {
                          openModal({
                            content: <EditStudyVersionPopupContainer closeModal={closeModal} />,
                            noButtons: true,
                          })
                        }}
                      />
                    </>
                  </Popup>
                </div>
              ) : null}
            </div>
          ) : null}
          {!isCurrentLive && (
            <Button
              id='promote-study'
              className='promote-study-btn'
              disabled={
                !hasAccessToEditVersionNotes ||
                !!error ||
                !notes ||
                (isCurrentDEV && !notesAreSaved) ||
                hasLiveVersionConflict()
              }
              content={STRINGS.promote}
              onClick={() => {
                if (isCurrentDEV && !notes) {
                  setError('Version change notes cannot be blank')

                  showError({ text: generateNotyMessage('Please check errors before proceeding', false) })

                  return
                }

                openModal({
                  content: isCurrentDEV ? STRINGS.doYouWantPromoteStudyToUat : STRINGS.doYouWantPromoteStudyToLive,
                  confirmButton: STRINGS.confirm,
                  cancelButton: STRINGS.cancel,
                  className: MODAL_CLASSES_MAP.confirmation,
                  onConfirm: handleCreateNewVersionClick,
                })
              }}
            />
          )}
        </div>
      </div>
    </Container>
  )
}

export default StudyVersionDetailsSection
